import React, { useState } from 'react';
import { CheckCircle2, XCircle, HelpCircle, ChevronRight } from 'lucide-react';

/**
 * Interaktive Zwischenfragen (Knowledge-Checks) in einer Lektion.
 *
 * Props:
 *   checks: Array<{
 *     question: string
 *     options: Array<{ id: string, text: string }>
 *     correct_option_id: string
 *     feedback_correct: string   // "Richtig! Das ist § 5 DGUV V1 …"
 *     feedback_wrong: string     // "Nicht ganz. Die richtige Antwort wäre …"
 *   }>
 *   onAllPassed: () => void      // wird aufgerufen sobald alle Fragen richtig
 *
 * Verhalten:
 *   - User muss alle Fragen beantworten, bevor "Weiter" freigeschaltet wird.
 *   - Falsche Antworten → Feedback + Retry (kein Lockout).
 *   - Wenn alle korrekt: `onAllPassed()` wird getriggert.
 */
export const KnowledgeChecks = ({ checks, onAllPassed }) => {
  const [answers, setAnswers] = useState({}); // { [qIdx]: optId }
  const [revealed, setRevealed] = useState({}); // { [qIdx]: true }
  const [locked, setLocked] = useState({});   // { [qIdx]: true when correct }

  // Defensive Normalisierung: manche Seed-Fragen haben `options: null` oder
  // fehlende `correct_option_id`. Ohne diese Guard-Rails crasht der Render
  // ("Cannot read properties of undefined (reading 'map')").
  const safeChecks = React.useMemo(
    () => (Array.isArray(checks) ? checks : []).map((c) => ({
      question: c?.question || '',
      options: Array.isArray(c?.options) ? c.options.filter(o => o && o.id) : [],
      correct_option_id: c?.correct_option_id ?? null,
      feedback_correct: c?.feedback_correct || '',
      feedback_wrong: c?.feedback_wrong || '',
    })).filter(c => c.question && c.options.length > 0 && c.correct_option_id != null),
    [checks]
  );

  if (safeChecks.length === 0) return null;

  const handleSelect = (qIdx, optId) => {
    if (locked[qIdx]) return; // Frage schon korrekt beantwortet — nicht mehr ändern
    setAnswers({ ...answers, [qIdx]: optId });
    setRevealed({ ...revealed, [qIdx]: true });
    if (optId === safeChecks[qIdx].correct_option_id) {
      const newLocked = { ...locked, [qIdx]: true };
      setLocked(newLocked);
      // Wenn alle Fragen korrekt → onAllPassed
      if (Object.keys(newLocked).length === safeChecks.length) {
        setTimeout(() => onAllPassed && onAllPassed(), 400);
      }
    }
  };

  const allCorrect = Object.keys(locked).length === safeChecks.length;

  return (
    <div
      className="mt-8 rounded-xl border border-yellow-500/30 bg-yellow-500/5 p-5 md:p-6"
      data-testid="knowledge-checks-container"
    >
      <div className="flex items-center gap-2 mb-4">
        <HelpCircle className="w-5 h-5 text-yellow-400" />
        <h3 className="text-yellow-300 font-bold text-lg">
          Wissens-Check ({Object.keys(locked).length}/{safeChecks.length})
        </h3>
      </div>
      <p className="text-gray-300 text-sm mb-5">
        Beantworte diese Fragen, um zur nächsten Lektion zu gelangen. Bei einer falschen Antwort erfährst du die richtige Lösung und kannst nochmal probieren.
      </p>

      {safeChecks.map((chk, qIdx) => {
        const selected = answers[qIdx];
        const isRevealed = revealed[qIdx];
        const isCorrect = selected === chk.correct_option_id;
        const done = locked[qIdx];

        return (
          <div
            key={qIdx}
            className="mb-6 last:mb-0 border border-white/10 rounded-lg p-4 bg-black/30"
            data-testid={`knowledge-check-${qIdx}`}
          >
            <p className="text-white font-semibold mb-3">
              <span className="text-yellow-400 mr-2">{qIdx + 1}.</span>
              {chk.question}
            </p>
            <div className="grid gap-2">
              {chk.options.map((opt) => {
                const isSelected = selected === opt.id;
                const isTheCorrect = opt.id === chk.correct_option_id;
                let cls = 'border-white/15 bg-black/40 text-gray-200 hover:border-yellow-500/50';
                if (isRevealed) {
                  if (isTheCorrect) cls = 'border-green-500/60 bg-green-500/15 text-green-100';
                  else if (isSelected) cls = 'border-red-500/60 bg-red-500/15 text-red-100';
                  else cls = 'border-white/10 bg-black/30 text-gray-400';
                }
                return (
                  <button
                    key={opt.id}
                    onClick={() => handleSelect(qIdx, opt.id)}
                    disabled={done}
                    className={`text-left px-4 py-3 rounded-lg border transition-colors ${cls} ${done ? 'cursor-not-allowed opacity-90' : 'cursor-pointer'}`}
                    data-testid={`knowledge-check-${qIdx}-option-${opt.id}`}
                  >
                    <span className="font-mono text-xs mr-2 text-yellow-500">{opt.id.toUpperCase()}</span>
                    {opt.text}
                    {isRevealed && isTheCorrect && (
                      <CheckCircle2 className="w-4 h-4 inline-block ml-2 text-green-400" />
                    )}
                    {isRevealed && isSelected && !isCorrect && (
                      <XCircle className="w-4 h-4 inline-block ml-2 text-red-400" />
                    )}
                  </button>
                );
              })}
            </div>
            {isRevealed && (
              <div
                className={`mt-3 rounded-md p-3 text-sm ${isCorrect ? 'bg-green-500/10 border border-green-500/30 text-green-100' : 'bg-red-500/10 border border-red-500/30 text-red-100'}`}
                data-testid={`knowledge-check-${qIdx}-feedback`}
              >
                <strong>{isCorrect ? 'Richtig!' : 'Nicht ganz.'}</strong>{' '}
                {isCorrect ? (chk.feedback_correct || 'Sehr gut.') : (chk.feedback_wrong || 'Versuch es nochmal.')}
              </div>
            )}
          </div>
        );
      })}

      {allCorrect && (
        <div
          className="mt-4 flex items-center gap-2 text-green-300 text-sm"
          data-testid="knowledge-checks-all-passed"
        >
          <CheckCircle2 className="w-5 h-5" />
          <span>Alle Fragen richtig beantwortet — du kannst zur nächsten Lektion.</span>
        </div>
      )}
    </div>
  );
};


/**
 * Responsives YouTube-Embed für Lektionsvideos.
 *
 * Nimmt einen `video_url` in verschiedenen Formen entgegen:
 *   - https://www.youtube.com/watch?v=ABC123
 *   - https://youtu.be/ABC123
 *   - https://youtube.com/embed/ABC123
 *   - ABC123 (nur Video-ID)
 * und extrahiert die Video-ID.
 */
export const LessonVideo = ({ videoUrl, title }) => {
  if (!videoUrl) return null;

  const extractYoutubeId = (url) => {
    if (!url) return null;
    if (/^[A-Za-z0-9_-]{11}$/.test(url)) return url;
    try {
      const u = new URL(url);
      if (u.hostname.includes('youtu.be')) return u.pathname.slice(1);
      const v = u.searchParams.get('v');
      if (v) return v;
      const m = u.pathname.match(/\/embed\/([A-Za-z0-9_-]{11})/);
      if (m) return m[1];
      return null;
    } catch {
      // Kein valides URL — evtl. schon nur die ID
      const m = String(url).match(/([A-Za-z0-9_-]{11})/);
      return m ? m[1] : null;
    }
  };

  const videoId = extractYoutubeId(videoUrl);
  if (!videoId) return null;

  return (
    <div
      className="relative w-full mb-6 rounded-xl overflow-hidden border border-white/10 shadow-lg bg-black"
      style={{ paddingBottom: '56.25%' }}
      data-testid="lesson-video-container"
    >
      <iframe
        className="absolute inset-0 w-full h-full"
        src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1`}
        title={title || 'Lektions-Video'}
        frameBorder="0"
        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        data-testid="lesson-video-iframe"
      />
    </div>
  );
};

export default KnowledgeChecks;
